import React from 'react';
import { Check, X } from 'lucide-react';
import RichContent, { isHtml } from '../RichText/RichContent';

// ── Question display shared by Practice and Remember ──────────────────────────
// Same markup and class names as CardBrowser (styles come from CardBrowser.css),
// so a question looks identical here and in the browser. CardBrowser itself is untouched.

export const MCQ_TYPES = [
  'MCQ', 'Statement-Based', 'Statement-Based-Classic', 'Statement-Based-Explanatory',
  'How-Many', 'Pair-Matching', 'Match-Following', 'Assertion-Reason',
];
export const isMCQCard = c => MCQ_TYPES.includes(c?.questionType) && c?.options?.length > 0;

/* Card fields can be strings, arrays or small objects: always turn them into text before rendering */
export const toText = v => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(toText).filter(Boolean).join('\n');
  if (typeof v === 'object') return toText(v.text ?? v.value ?? v.label ?? v.html ?? v.content ?? v.option ?? '');
  return '';
};

function splitIntroClosing(text) {
  if (!text) return { intro: '', closing: '' };
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length <= 1) return { intro: text, closing: '' };
  return { intro: lines[0], closing: lines.slice(1).join('\n') };
}

/* ── Table parsing: pipe tables, tab tables and "List I / List II" text become real tables ── */
const SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const LIST1 = /^\W*list\s*[-–—]?\s*(?:i|1)\b/i;
const LIST2 = /list\s*[-–—]?\s*(?:ii|2)\b/i;
const LEFT = /^\(?([A-Ea-e])[.)]\)?\s+(.+)$/;
const RIGHT = /^\(?([1-9])[.)]\)?\s+(.+)$/;
const cells = line => line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(c => c.trim());

function tryTable(lines, i) {
  const line = lines[i].trim();
  const next = (lines[i + 1] || '').trim();

  if (line.includes('|') && cells(line).length >= 2) {
    if (SEP.test(next)) {
      const rows = [];
      let j = i + 2;
      while (j < lines.length && lines[j].includes('|')) { rows.push(cells(lines[j])); j += 1; }
      return { head: cells(line), rows, next: j };
    }
    if (next.includes('|') && cells(next).length >= 2) {
      const all = [];
      let j = i;
      while (j < lines.length && lines[j].includes('|') && !SEP.test(lines[j].trim())) { all.push(cells(lines[j])); j += 1; }
      if (all.length >= 2) return { head: all[0], rows: all.slice(1), next: j };
    }
  }

  if (line.includes('\t') && next.includes('\t')) {
    const all = [];
    let j = i;
    while (j < lines.length && lines[j].includes('\t')) { all.push(lines[j].trim().split(/\t+/)); j += 1; }
    return { head: all[0], rows: all.slice(1), next: j };
  }

  if (LIST1.test(line)) {
    if (LIST2.test(line)) {
      const head = [line.slice(0, line.search(LIST2)).trim(), line.slice(line.search(LIST2)).trim()];
      const rows = [];
      const lefts = [];
      const rights = [];
      let j = i + 1;
      while (j < lines.length) {
        const t = lines[j].trim();
        if (!t) break;
        const parts = t.split(/\t+|\s{2,}/);
        const both = t.match(/^\(?([A-Ea-e])[.)]\)?\s+(.+?)\s+\(?([1-9])[.)]\)?\s+(.+)$/);
        if (parts.length >= 2 && LEFT.test(parts[0]) && RIGHT.test(parts[1])) rows.push([parts[0], parts[1]]);
        else if (both) rows.push([`${both[1]}. ${both[2]}`, `${both[3]}. ${both[4]}`]);
        else if (LEFT.test(t) && !rights.length) lefts.push(t);
        else if (RIGHT.test(t)) rights.push(t);
        else break;
        j += 1;
      }
      for (let r = 0; r < Math.max(lefts.length, rights.length); r += 1) rows.push([lefts[r] || '', rights[r] || '']);
      if (rows.length) return { head, rows, next: j };
    } else {
      const lefts = [];
      let j = i + 1;
      while (j < lines.length && LEFT.test(lines[j].trim())) { lefts.push(lines[j].trim()); j += 1; }
      if (lefts.length && j < lines.length && LIST2.test(lines[j].trim())) {
        const head = [line, lines[j].trim()];
        const rights = [];
        j += 1;
        while (j < lines.length && RIGHT.test(lines[j].trim())) { rights.push(lines[j].trim()); j += 1; }
        if (rights.length) return { head, rows: lefts.map((l, r) => [l, rights[r] || '']), next: j };
      }
    }
  }
  return null;
}

function TableBlock({ t }) {
  return (
    <div className="cb-match">
      <table>
        <thead><tr>{t.head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
        <tbody>{t.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function FormattedText({ text }) {
  if (!text) return null;
  if (isHtml(text)) return <div className="cb-rich"><RichContent html={text} /></div>;
  const lines = text.split('\n');
  const out = [];
  for (let i = 0; i < lines.length;) {
    const t = lines[i].trim();
    if (!t) { out.push(<div key={i} className="cb-gap" />); i += 1; continue; }
    const tb = tryTable(lines, i);
    if (tb) { out.push(<TableBlock key={i} t={tb} />); i = tb.next; continue; }
    const num = t.match(/^(\d+)[.)]\s/);
    const bullet = /^[•\-*→]\s/.test(t);
    const clean = t.replace(/^\d+[.)]\s*|^[•\-*→]\s*|\*\*/g, '').trim();
    if (num) out.push(<div key={i} className="cb-li"><b>{num[1]}.</b><span>{clean}</span></div>);
    else if (bullet) out.push(<div key={i} className="cb-li"><b>•</b><span>{clean}</span></div>);
    else out.push(<p key={i}>{t}</p>);
    i += 1;
  }
  return <div className="cb-text">{out}</div>;
}

function FormattedQuestion({ text }) {
  if (!text) return null;
  if (isHtml(text)) return <div className="cb-qtext cb-rich"><RichContent html={text} /></div>;
  const lines = text.split('\n');
  const out = [];
  for (let i = 0; i < lines.length;) {
    const t = lines[i].trim();
    if (!t) { i += 1; continue; }
    const tb = tryTable(lines, i);
    if (tb) { out.push(<TableBlock key={i} t={tb} />); i = tb.next; continue; }
    const num = t.match(/^(\d+)[.)]\s/);
    const clean = t.replace(/^\d+[.)]\s*|\*\*/g, '').trim();
    if (num) out.push(<div key={i} className="cb-li"><b>{num[1]}.</b><span>{clean}</span></div>);
    else out.push(<p key={i} className={/^[A-Z][^a-z]{4,}/.test(t) ? 'cb-strong' : ''}>{t.replace(/\*\*/g, '')}</p>);
    i += 1;
  }
  return <div className="cb-qtext">{out}</div>;
}

// ── The question box: tags, question, options ─────────────────────────────────
// picked: index the user chose (null if the answer was only revealed) · done: answer side visible
export function QuestionCard({ card, picked, done, onPick, showTags = true, actions }) {
  const mcq     = isMCQCard(card);
  const options = mcq ? card.options.map(toText) : [];
  const correct = mcq ? card.options.findIndex(o => o && o.isCorrect === true) : -1;
  const stmts   = (Array.isArray(card.statements) ? card.statements : []).map(toText);
  const { intro, closing } = stmts.length ? splitIntroClosing(toText(card.question)) : { intro: '', closing: '' };
  const long    = options.some(o => o.length > 48);

  return (
    <section className="cb-card">
      {(showTags || actions) && (
        <div className="cb-card-heading">
          {showTags && (
            <div className="cb-tags">
              {toText(card.subject) && <span>{toText(card.subject)}</span>}
              {toText(card.examType) && <span>{toText(card.examType)}</span>}
              {toText(card.difficulty) && <span>{toText(card.difficulty)}</span>}
            </div>
          )}
          {actions && <div className="cb-card-actions">{actions}</div>}
        </div>
      )}

      <div className="cb-q">
        {stmts.length ? (
          <>
            <FormattedQuestion text={intro} />
            <ol className="cb-stmts">{stmts.map((s, i) => <li key={i}>{s}</li>)}</ol>
            {closing && <FormattedQuestion text={closing} />}
          </>
        ) : <FormattedQuestion text={toText(card.question)} />}
      </div>

      {mcq && (
        <div className={`cb-opts ${long ? 'list' : ''}`} role="group" aria-label="Options">
          {options.map((opt, i) => {
            let st = '';
            if (done) st = i === correct ? 'ok' : i === picked ? 'bad' : 'dim';
            return (
              <button key={i} type="button" className={`cb-opt ${st} ${done ? 'locked' : ''}`}
                disabled={done} aria-pressed={i === picked} onClick={() => onPick(i)}>
                <b>{String.fromCharCode(65 + i)}</b>
                <span>{opt}</span>
                {st === 'ok' && <Check size={17} role="img" aria-label="Correct" />}
                {st === 'bad' && <X size={17} role="img" aria-label="Wrong" />}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ── Explanation / model answer, outside the box like CardBrowser ──────────────
export function AnswerBlock({ card }) {
  const mcq   = isMCQCard(card);
  const main  = toText(mcq ? (card.explanation || card.answer) : (card.modelAnswer || card.answer));
  const extra = mcq ? '' : toText(card.explanation);
  if (!main && !extra) return null;
  return (
    <div className="cb-a">
      <FormattedText text={main} />
      {extra && extra !== main && <FormattedText text={extra} />}
    </div>
  );
}